import {useKpisMonthlyTrendsByTypes} from '@domain/kpis'
import {Select} from '@hconnect/common/components'
import {DataContentWrapper} from '@hconnect/common/containers'
import {KpiType} from '@hconnect/common/types'
import {CardBox} from '@hconnect/uikit/src/lib2'
import {spawnErrorToast} from '@hm/fundamental'
import {Stack, Typography} from '@mui/material'
import {Moment} from 'moment-timezone'
import {useCallback} from 'react'
import {useTranslation} from 'react-i18next'

import {CurrentVsLastYearKpiChart} from '../components'
import {FEATURE_NAMESPACE, PERFORMANCE_REVIEW_KPI_TYPES} from '../consts'

type Props = {
  plantId: string
  selectedKpi: KpiType
  selectedDate: Moment
  onSelectedKpiChange: (kpi?: KpiType) => void
  enableKpiSelection?: boolean
  commentsSlot?: React.ReactNode
}

export const KpiTrendContainer = ({
  plantId,
  selectedKpi,
  selectedDate,
  enableKpiSelection = false,
  onSelectedKpiChange,
  commentsSlot
}: Props) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const notifyFailedToFetchKpis = useCallback(
    () => spawnErrorToast(t('kpis.message.failedToFetchKpis')),
    [t]
  )

  const kpiOptions = PERFORMANCE_REVIEW_KPI_TYPES.map((kpi) => ({
    label: t(`kpis.label.${kpi}`),
    value: kpi
  }))

  const currentYearNumber = selectedDate.year()

  const startOfLastYear = selectedDate.clone().subtract(1, 'year').startOf('year')
  const startOfNextYear = selectedDate.clone().add(1, 'year').startOf('year')

  const {
    data = [],
    isLoading,
    isError,
    isRefetching,
    refetch
  } = useKpisMonthlyTrendsByTypes(
    {
      plantId,
      from: startOfLastYear,
      to: startOfNextYear,
      kpiTypes: [selectedKpi]
    },
    {onError: notifyFailedToFetchKpis}
  )

  const currentData = data[0]

  return (
    <CardBox data-test-id="kpi-trend-card">
      <Stack spacing={3}>
        {enableKpiSelection ? (
          <Select
            options={kpiOptions}
            value={selectedKpi}
            onChange={onSelectedKpiChange}
            label={t('filters.label.kpi')}
            data-test-id="kpi-select"
          />
        ) : (
          <Typography variant="h3">{t(`kpis.label.${selectedKpi}`)}</Typography>
        )}
        <DataContentWrapper
          showLoader={isLoading}
          isError={isError}
          isRefetching={isRefetching}
          retryFunction={refetch}
          data={currentData}
          progressSx={{color: 'primary.main', width: '100%'}}
        >
          <CurrentVsLastYearKpiChart data={currentData} currentYear={currentYearNumber} />
        </DataContentWrapper>
        {commentsSlot}
      </Stack>
    </CardBox>
  )
}
